import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  ACCOUNT_DATA_EXPORT_FUNCTION,
  EXPORT_POLL_MS,
  exportErrorMessage,
  type ExportStatus,
  isExportActive,
} from "@/lib/account-data-export";

async function errorCode(error: unknown): Promise<string | null> {
  if (error && typeof error === "object" && "context" in error && error.context instanceof Response) {
    try {
      const body = await error.context.json();
      return typeof body?.error === "string" ? body.error : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function useAccountDataExport(organizationId: string | null) {
  const [status, setStatus] = useState<ExportStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    const { data, error: invokeError } = await supabase.functions.invoke(ACCOUNT_DATA_EXPORT_FUNCTION, {
      body: { action: "status", organization_id: organizationId },
    });
    if (invokeError) setError(exportErrorMessage(await errorCode(invokeError)));
    else setStatus(data as ExportStatus);
    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  const active = isExportActive(status?.job);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => void refresh(), EXPORT_POLL_MS);
    return () => clearInterval(id);
  }, [active, refresh]);

  const request = useCallback(async () => {
    if (!organizationId) return;
    setRequesting(true);
    const { error: invokeError } = await supabase.functions.invoke(ACCOUNT_DATA_EXPORT_FUNCTION, {
      body: { action: "request", organization_id: organizationId },
    });
    if (invokeError) setError(exportErrorMessage(await errorCode(invokeError)));
    else setError(null);
    await refresh();
    setRequesting(false);
  }, [organizationId, refresh]);

  return { status, loading, requesting, error, request, refresh };
}
